import { VStack } from '@chakra-ui/react';
import { GPSCoordinatesListField } from '@components/ui/coordinatesInput/list/GPSCoordinatesListField';
import { FieldArray, useFormikContext } from 'formik';
import { MultiPolygon } from 'geojson';

import { CoordinatesUnit } from '@/types/commons/commons.types';

interface MultiPolygonCoordinatesFieldProps<
  TValues extends Record<string, MultiPolygon | undefined>,
  K extends keyof TValues & string,
> {
  name: K;
  unit: CoordinatesUnit;
  setUnit: (unit: CoordinatesUnit) => void;
  setPolygon: (field: string, value: string) => void;
  deleteCoordinate: (pointIndex: number) => void;
  addCoordinate: (polygonIndex: number) => void;
}

export function MultiPolygonCoordinatesField<
  TValues extends Record<string, MultiPolygon | undefined>,
  K extends keyof TValues & string,
>({
  name,
  unit,
  setUnit,
  setPolygon,
  deleteCoordinate,
  addCoordinate,
}: Readonly<MultiPolygonCoordinatesFieldProps<TValues, K>>) {
  const { values } = useFormikContext<TValues>();
  const multiPolygon: MultiPolygon | undefined = values[name];
  const polygons: MultiPolygon['coordinates'] = multiPolygon?.coordinates ?? [];

  return (
    <FieldArray name={name}>
      {() => (
        <VStack align="stretch" gap={6}>
          {polygons.map((_, polygonIndex) => (
            <GPSCoordinatesListField
              key={`polygon-${polygonIndex}`}
              name={`${name}.coordinates[${polygonIndex}][0]`}
              unit={unit}
              setUnit={setUnit}
              setPolygon={setPolygon}
              deleteCoordinate={(coordIndex) => deleteCoordinate(coordIndex)}
              addCoordinate={() => addCoordinate(polygonIndex)}
            />
          ))}
        </VStack>
      )}
    </FieldArray>
  );
}
